import {ComponentFactoryResolver, ComponentRef, Injectable, Type, ViewContainerRef} from '@angular/core';
import {Subject} from 'rxjs';
import {IModalCloseSubject} from '@models/app/modal/IModalCloseSubject';
import {IModalData} from '@models/app/modal/IModalData';
import {IModalSettings} from '@models/app/modal/IModalSettings';
import {findParent} from '@app/shared/utils/dom/Queries';
import {environment} from '@environments/environment';
import {hasValue} from '@shared/utils/helpers';

/**
 *
 * Important notes about using the modal service...
 * ------------------------------------------------
 * Pushing Data into the modal, if you want data to be pushed in the modal, this can be achieved by calling the "open"
 * method while pushing in an object to the 'content' parameter. This will then set each object key set inside of the
 * modal component reference. NOTE: if you do not put @Input() before your attribute definitions in the modal you're
 * trying to open, you will only be able to access the instance variables after NgAfterViewInit, otherwise they're
 * accessible after ngOnInit :)
 */


@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private vcr: ViewContainerRef = null;
  private modalCloseSlugKey = 'closeSlug';

  public onClose: Subject<IModalCloseSubject>;
  openedModals: Array<ComponentRef<any>> = [];

  private tmpOutsideClickIgnoreClasses = null;
  private tmpOutsideClickIgnoreElements = null;

  constructor(private cfr: ComponentFactoryResolver) {
    this.onClose = new Subject();
    this.onClose.closed = true;
  }

  /**
   * Sets a custom ViewContainerReference for the modal service. You can use this to open a modal relevant to a specific component in
   * angular.
   * @param vcr
   */
  public set setVcr(vcr: ViewContainerRef) {
    this.vcr = vcr;
  }

  /**
   * Returns whether or not there are currently any modals that are open.
   */
  public get hasModalsOpen(): boolean {
    return this.openedModals && this.openedModals.length > 0;
  }

  open<C>(modalRef: Type<C>, content: object = {}, closeable = true, vcr: ViewContainerRef = null): void {
    this.onClose.closed = false;
    const modalIndex = this.openedModals.length;

    const modalData: IModalData = {
      ...content,
      ...{
        closeable,
        index: modalIndex
      }
    };

    this.setupModalSettings(modalData.modalSettings);

    // Removing the modalSettings from the modalData if found (so it doesn't throw errors when loading in the rest of the modal data)
    if (modalData.hasOwnProperty('modalSettings')) {
      delete modalData.modalSettings;
    }

    this.loadModal(modalRef, modalData, vcr);

    // Adding the event listener for closing outside of the modal
    if (modalData.closeable) {

      // This timeout is for events that deal with dragging and dropping (when the browser executes this code quicker than the render time)
      setTimeout(() => {
        this.addEventListener();
      }, 50);
    }
  }

  private setupModalSettings(settings: IModalSettings): void {
    this.tmpOutsideClickIgnoreClasses = [
      'e-datepicker',
      'e-popup',
      'e-calendar',
      'e-header',
      'e-content',
      'e-footer-container',
      'e-day',
      'e-icon-container',
      'e-list-item',
      'e-list-parent',
      'alert',
      'alert-close'
    ];

    this.tmpOutsideClickIgnoreElements = ['TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'SVG'];

    if (settings?.ignoreElementsForOutsideClick) {
      this.tmpOutsideClickIgnoreElements = [...this.tmpOutsideClickIgnoreElements, ...settings.ignoreElementsForOutsideClick];
    }

    if (settings?.ignoreClassesForOutsideClick) {
      this.tmpOutsideClickIgnoreClasses = [...this.tmpOutsideClickIgnoreClasses, ...settings.ignoreElementsForOutsideClick];
    }
  }

  /**
   * Handles the on click event of either a close button or outside of a modal
   * @param e
   * @private
   */
  private closeOnBtnAndOutsideClick = (e: PointerEvent) => {
    const modal = this.getCurrentModal();
    if (!modal || !modal.instance.closeable) {
      return;
    }

    const clickTarget: Element = e.target as Element;
    const clickedOutside = clickTarget.classList.contains('modal-hero');

    if (clickedOutside) {
      this.tmpOutsideClickIgnoreClasses = null;

      this.close(modal.instance.index);
    }
  }

  private closeOnEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      const modal = this.getCurrentModal();
      if (!modal.instance.closeable) {
        return;
      }

      this.close(modal.instance.index);
    }
  }

  private get lastOpenedModalIndex(): number {
    return this.openedModals.length - 1;
  }

  /**
   *
   * @param modalComponentRef The reference to the injectable modal component
   * @param modalData The attributes that will be loaded into the modal instance
   * @param vcr an overridable parameter for ViewContainerRef
   */
  loadModal<C>(modalComponentRef: Type<C>, modalData: IModalData, vcr: ViewContainerRef = null): void {
    const targetVcr = hasValue(vcr) ? vcr : this.vcr; // setting the default target view container reference.

    const component = targetVcr.createComponent<C>(modalComponentRef);

    const allowedInputProperties = ['index', 'closeable', 'closeSlug'];

    // Collecting all of the valid input properties
    const cf = this.cfr.resolveComponentFactory(modalComponentRef);

    for (const inputObj of cf.inputs) {
      allowedInputProperties.push(inputObj.propName);
    }

    // Adding all the variables from the content attribute into the component
    const protectedVars = ['modal'];
    Object.keys(modalData).forEach(key => {
      if (!allowedInputProperties.includes(key)) {
        if (!environment.production) {
          const msg = `The key '${key}' does not contain a matching '@Input()' qualifier in the modal you're trying to open.`;
          console.error(msg);
          return;
        }
      }

      if (!protectedVars.includes(key)) {
        component.instance[key] = modalData[key];
      }
    });

    this.openedModals.push(component);
  }

  /**
   * Checks whether or not a given modal is currently open.
   * @param slug the slug of the modal being checked.
   */
  modalIsCurrentlyOpen(slug: string): boolean {
    if (slug === null || typeof slug === 'undefined') {
      return false;
    }

    const indexOfSlugModal = this.openedModals.findIndex((modal) => {
      return modal.instance[this.modalCloseSlugKey] === slug;
    });

    return indexOfSlugModal !== -1;
  }

  /**
   * In order to use this function, put an attribute on your modal component: "closeSlug" that will represent an index capability
   * to close your specific modal. As seen in the function below, this will then search the currently open modal instances with
   * the slug that you've provided.
   * @param slug string
   */
  closeModalBySlug(slug: string): void {
    const indexOfSlugModal = this.openedModals.findIndex((modal) => {
      return modal.instance[this.modalCloseSlugKey] === slug;
    });
    const modalFound = indexOfSlugModal !== -1;
    if (modalFound) {
      this.close(indexOfSlugModal);
    }
  }

  closeAllBySlug(slug: string): void {

    const newOpenedModals: ComponentRef<any>[] = [];
    for (const [index, modal] of this.openedModals.entries()) {
      if (modal.instance[this.modalCloseSlugKey] === slug) {
        this.close(index, false);
      } else {
        newOpenedModals.push(modal);
      }
    }

    this.openedModals = newOpenedModals;
  }

  /**
   * If the index is null, the modal that will be closed will be the current modal
   * @param index number of the modal
   * @param removeItem remove the closed modal from the list of open modals (with splice)
   */
  close(index = null, removeItem = true): void {
    index = index ? index : this.lastOpenedModalIndex;

    const modal = this.getModal(index);
    if (!modal) {
      return;
    }

    // todo check the @Output fields instead of passing the whole modal instance
    this.onClose.next({
      closed: true,
      content: modal.instance
    });

    // destroying the modal component instance
    modal.destroy();

    if (removeItem) {
      this.openedModals.splice(index, 1);
    }

    if (this.openedModals.length === 0) {
      this.removeEventListener();
    }
  }

  public closeAll(): void {
    this.removeEventListener();
    this.openedModals.forEach((componentRef, index) => {
      this.close(index);
    });
  }

  /**
   * gets a modal based on the index
   * @param index number
   */
  public getModal(index: number): ComponentRef<any> {
    const maxIndex = this.openedModals.length - 1;
    if (index > maxIndex || index < 0) {  // Modal not found
      return null;
    }

    return this.openedModals[index];
  }

  public getCurrentModal(): ComponentRef<any> {
    const lastOpenedModalIndex = this.lastOpenedModalIndex;
    return this.getModal(lastOpenedModalIndex);
  }

  private addEventListener(): void {
    document.body.addEventListener('click', this.closeOnBtnAndOutsideClick);
    document.addEventListener('keydown', this.closeOnEscape);
  }

  private removeEventListener(): void {
    document.body.removeEventListener('click', this.closeOnBtnAndOutsideClick);
    document.removeEventListener('keydown', this.closeOnEscape);

  }
}
